import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Users, IndianRupee, Phone, Mail, User, CheckCircle, Check, BedDouble } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPE_LABEL = { deluxe: 'Deluxe', standard: 'Standard', couple: 'Couple' };
const canHaveExtraBed = (r) => r && (r.room_type === 'deluxe' || r.room_type === 'standard');

const BookingModal = ({ isOpen, onClose, room }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [allRooms, setAllRooms] = useState([]);
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [extraBeds, setExtraBeds] = useState({}); // { roomId: true }
  const [formData, setFormData] = useState({
    guest_name: '',
    email: '',
    phone: '',
    check_in_date: null,
    check_out_date: null,
    num_guests: 2,
    special_requests: ''
  });
  const [errors, setErrors] = useState({});

  // fetch all rooms once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/rooms`);
        setAllRooms(res.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // when opened for a room, pre-select it
  useEffect(() => {
    if (room) {
      setSelectedRoomIds([room.id]);
      setExtraBeds({});
      setFormData(prev => ({ ...prev, num_guests: 2 }));
    }
  }, [room]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSuccess(false);
      setErrors({});
    }
  }, [isOpen]);

  const EXTRA_BED_PRICE = 1000; // per night, per extra bed

const roomsList = allRooms.length ? allRooms : (room ? [room] : []);
  const selectedRooms = roomsList.filter(r => selectedRoomIds.includes(r.id));
  const extraBedCount = selectedRooms.filter(r => canHaveExtraBed(r) && extraBeds[r.id]).length;
  // Each room holds 2 guests; an extra bed (deluxe/standard only) allows a 3rd guest.
  const maxGuests = selectedRooms.reduce(
    (sum, r) => sum + 2 + (canHaveExtraBed(r) && extraBeds[r.id] ? 1 : 0),
    0
  ) || 2;

  const nights = () => {
    if (!formData.check_in_date || !formData.check_out_date) return 0;
    return Math.max(
      Math.ceil((new Date(formData.check_out_date) - new Date(formData.check_in_date)) / (1000 * 60 * 60 * 24)),
      1
    );
  };

  const calculateTotal = () => {
    const n = nights();
    if (!n || !selectedRooms.length) return 0;
    const roomTotal = selectedRooms.reduce((sum, r) => sum + (r.price_per_head || 0), 0) * n;
    const bedTotal = extraBedCount * EXTRA_BED_PRICE * n;
    return roomTotal + bedTotal;
  };

  // keep guest count within the allowed maximum as rooms / extra beds change
  useEffect(() => {
    if (typeof formData.num_guests === 'number' && formData.num_guests > maxGuests) {
      setFormData(prev => ({ ...prev, num_guests: maxGuests }));
    }
  }, [maxGuests]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRoom = (r) => {
    setSelectedRoomIds(prev =>
      prev.includes(r.id) ? prev.filter(id => id !== r.id) : [...prev, r.id]
    );
    if (errors.rooms) setErrors(prev => ({ ...prev, rooms: null }));
  };

  const toggleExtraBed = (r) => {
    setExtraBeds(prev => ({ ...prev, [r.id]: !prev[r.id] }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.guest_name.trim()) newErrors.guest_name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, '')))
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!selectedRoomIds.length) newErrors.rooms = 'Please select at least one room';
    if (!formData.check_in_date) newErrors.check_in_date = 'Check-in date is required';
    if (!formData.check_out_date) newErrors.check_out_date = 'Check-out date is required';
    if (formData.check_in_date && formData.check_out_date) {
      if (new Date(formData.check_in_date) >= new Date(formData.check_out_date)) {
        newErrors.check_out_date = 'Check-out must be after check-in';
      }
    }
    if (formData.num_guests < 1) newErrors.num_guests = 'At least 1 guest required';
    if (formData.num_guests > maxGuests) newErrors.num_guests = `Maximum ${maxGuests} guests for the selected room(s)`;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bedRooms = selectedRooms.filter(r => canHaveExtraBed(r) && extraBeds[r.id]).map(r => r.name);
      const extraBedNote = bedRooms.length ? `Extra bed requested for: ${bedRooms.join(', ')}.` : '';
      const requests = [formData.special_requests?.trim(), extraBedNote].filter(Boolean).join(' ');

      const bookingData = {
        guest_name: formData.guest_name,
        email: formData.email,
        phone: formData.phone,
        check_in_date: formData.check_in_date ? format(formData.check_in_date, 'yyyy-MM-dd') : '',
        check_out_date: formData.check_out_date ? format(formData.check_out_date, 'yyyy-MM-dd') : '',
        num_guests: formData.num_guests,
        room_type: (selectedRooms[0] || room).room_type,
        room_id: (selectedRooms[0] || room).id,
        room_name: (selectedRooms[0] || room).name,
        room_ids: selectedRoomIds,
        room_names: selectedRooms.map(r => r.name),
        extra_beds: extraBedCount,
        special_requests: requests,
        total_price: calculateTotal()
      };
      await axios.post(`${API_URL}/api/bookings`, bookingData);
      setSuccess(true);
      setStep(4);
    } catch (error) {
      console.error('Booking error:', error);
      setErrors({ submit: 'Failed to submit booking. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  if (!room) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#121816] border-[#D4AF37]/40 max-h-[90vh] overflow-y-auto text-[#E8E3D9]">
        <DialogHeader>
          <div className="overline mb-2">{success ? 'Confirmed' : 'Reservation'}</div>
          <DialogTitle className="font-serif text-2xl text-[#E8E3D9] font-light">
            {success ? 'Booking received' : room.name}
          </DialogTitle>
        </DialogHeader>

        {!success && (
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 flex items-center justify-center text-xs font-medium border transition-colors ${
                  step >= s ? 'bg-[#D4AF37] text-[#0B0F0E] border-[#D4AF37]' : 'bg-transparent text-[#8E9A95] border-[#D4AF37]/30'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-px mx-2 ${step > s ? 'bg-[#D4AF37]' : 'bg-[#D4AF37]/20'}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="guest_name" className="text-[#C4BFB1] text-xs tracking-wider uppercase">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" size={16} />
                <Input
                  id="guest_name"
                  value={formData.guest_name}
                  onChange={(e) => handleChange('guest_name', e.target.value)}
                  placeholder="Enter your full name"
                  className="pl-10 bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] focus:border-[#D4AF37]"
                  data-testid="booking-name-input"
                />
              </div>
              {errors.guest_name && <p className="text-red-400 text-xs mt-1">{errors.guest_name}</p>}
            </div>

            <div>
              <Label htmlFor="email" className="text-[#C4BFB1] text-xs tracking-wider uppercase">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" size={16} />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your.email@example.com"
                  className="pl-10 bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] focus:border-[#D4AF37]"
                  data-testid="booking-email-input"
                />
              </div>
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#C4BFB1] text-xs tracking-wider uppercase">Phone Number</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" size={16} />
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="10-digit mobile number"
                  className="pl-10 bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] focus:border-[#D4AF37]"
                  data-testid="booking-phone-input"
                />
              </div>
              {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
            </div>

            <Button
              onClick={handleNext}
              className="w-full bg-[#D4AF37] hover:bg-[#E5C766] text-[#0B0F0E] uppercase tracking-[0.2em] text-xs font-semibold py-6"
              data-testid="booking-next-step1"
            >
              Continue
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {/* ROOM SELECTION */}
            <div>
              <Label className="text-[#C4BFB1] text-xs tracking-wider uppercase">Select Room(s)</Label>
              <p className="text-[10px] text-[#8E9A95] mt-1 mb-2 tracking-wider">Choose one or more rooms for your stay.</p>
              <div className="space-y-2" data-testid="booking-rooms-list">
                {roomsList.map((r) => {
                  const selected = selectedRoomIds.includes(r.id);
                  return (
                    <div key={r.id} className={`border transition-colors ${selected ? 'border-[#D4AF37]/60 bg-[#0B0F0E]' : 'border-[#D4AF37]/20 bg-[#0B0F0E]/40'}`}>
                      <button
                        type="button"
                        onClick={() => toggleRoom(r)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
                        data-testid={`booking-room-select-${r.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-5 h-5 flex items-center justify-center border ${selected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#D4AF37]/40'}`}>
                            {selected && <Check size={13} className="text-[#0B0F0E]" />}
                          </span>
                          <span>
                            <span className="text-[#E8E3D9] text-sm">{r.name}</span>
                            <span className="block text-[10px] tracking-wider uppercase text-[#8E9A95]">{ROOM_TYPE_LABEL[r.room_type] || r.room_type}</span>
                          </span>
                        </div>
                        <span className="text-[#D4AF37] text-sm numeral">₹{(r.price_per_head || 0).toLocaleString()}</span>
                      </button>

                      {/* extra bed — only deluxe/standard */}
                      {selected && (
                        canHaveExtraBed(r) ? (
                          <button
                            type="button"
                            onClick={() => toggleExtraBed(r)}
                            className="w-full flex items-center gap-2 px-3 py-2 border-t border-[#D4AF37]/15 text-left"
                            data-testid={`booking-extra-bed-${r.id}`}
                          >
                            <span className={`w-4 h-4 flex items-center justify-center border ${extraBeds[r.id] ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#D4AF37]/40'}`}>
                              {extraBeds[r.id] && <Check size={11} className="text-[#0B0F0E]" />}
                            </span>
                            <BedDouble size={13} className="text-[#D4AF37]" />
                            <span className="text-[11px] text-[#C4BFB1]">Add an extra bed <span className="text-[#D4AF37]">(+₹1,000/night · allows 3rd guest)</span></span>
                          </button>
                        ) : (
                          <div className="px-3 py-2 border-t border-[#D4AF37]/15 text-[10px] tracking-wider text-[#8E9A95] italic">
                            Extra bed not available for couple rooms
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
              {errors.rooms && <p className="text-red-400 text-xs mt-1">{errors.rooms}</p>}
            </div>

            <div>
              <Label className="text-[#C4BFB1] text-xs tracking-wider uppercase">Check-in Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1 bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] hover:bg-[#1A2421] hover:text-[#E8E3D9]"
                    data-testid="booking-checkin-btn"
                  >
                    <Calendar className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    {formData.check_in_date ? format(formData.check_in_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121816] border-[#D4AF37]/40 z-[200]" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={formData.check_in_date}
                    onSelect={(date) => {
                      handleChange('check_in_date', date);
                      if (formData.check_out_date && date && new Date(formData.check_out_date) <= new Date(date)) {
                        handleChange('check_out_date', null);
                      }
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[10px] text-[#8E9A95] mt-1 tracking-wider">Check-in time: 1:00 PM</p>
              {errors.check_in_date && <p className="text-red-400 text-xs mt-1">{errors.check_in_date}</p>}
            </div>

            <div>
              <Label className="text-[#C4BFB1] text-xs tracking-wider uppercase">Check-out Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1 bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] hover:bg-[#1A2421] hover:text-[#E8E3D9]"
                    data-testid="booking-checkout-btn"
                  >
                    <Calendar className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    {formData.check_out_date ? format(formData.check_out_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121816] border-[#D4AF37]/40 z-[200]" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={formData.check_out_date}
                    onSelect={(date) => handleChange('check_out_date', date)}
                    disabled={(date) => date <= (formData.check_in_date || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[10px] text-[#8E9A95] mt-1 tracking-wider">Check-out time: 11:00 AM</p>
              {errors.check_out_date && <p className="text-red-400 text-xs mt-1">{errors.check_out_date}</p>}
            </div>

            <div>
              <Label htmlFor="num_guests" className="text-[#C4BFB1] text-xs tracking-wider uppercase">Number of Guests</Label>
              <div className="relative mt-1">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" size={16} />
                <Input
                  id="num_guests"
                  type="number"
                  min={1}
                  max={maxGuests}
                  value={formData.num_guests === '' ? '' : formData.num_guests}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange('num_guests', val === '' ? '' : parseInt(val) || '');
                  }}
                  onBlur={() => {
                    if (formData.num_guests === '' || formData.num_guests < 1) handleChange('num_guests', 1);
                  }}
                  className="pl-10 bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] focus:border-[#D4AF37]"
                  data-testid="booking-guests-input"
                />
              </div>
              <p className="text-[10px] text-[#8E9A95] mt-1 tracking-wider">
                2 guests per room · add an extra bed (+₹1,000/night) for a 3rd · up to {maxGuests} total
              </p>
              {errors.num_guests && <p className="text-red-400 text-xs mt-1">{errors.num_guests}</p>}
            </div>

            <div>
              <Label htmlFor="special_requests" className="text-[#C4BFB1] text-xs tracking-wider uppercase">Special Requests (Optional)</Label>
              <div className="relative mt-1">
                <Textarea
                  id="special_requests"
                  value={formData.special_requests}
                  onChange={(e) => handleChange('special_requests', e.target.value)}
                  placeholder="Any special requirements..."
                  className="bg-[#0B0F0E] border-[#D4AF37]/30 text-[#E8E3D9] focus:border-[#D4AF37] min-h-[80px]"
                  data-testid="booking-requests-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border-[#D4AF37]/30 bg-transparent text-[#C4BFB1] hover:bg-[#1A2421] hover:text-[#E8E3D9] uppercase tracking-[0.2em] text-xs"
                data-testid="booking-back-step2"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C766] text-[#0B0F0E] uppercase tracking-[0.2em] text-xs font-semibold"
                data-testid="booking-next-step2"
              >
                Review
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-[#0B0F0E] border border-[#D4AF37]/30 p-5">
              <div className="overline mb-4">Summary</div>

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between items-start">
                  <span className="text-[#8E9A95]">Room(s)</span>
                  <span className="text-[#E8E3D9] text-right">
                    {selectedRooms.map(r => (
                      <span key={r.id} className="block">
                        {r.name}
                        {canHaveExtraBed(r) && extraBeds[r.id] && <span className="text-[#D4AF37] text-xs"> + extra bed</span>}
                      </span>
                    ))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8E9A95]">Guest</span>
                  <span className="text-[#E8E3D9]">{formData.guest_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8E9A95]">Check-in</span>
                  <span className="text-[#E8E3D9]">{formData.check_in_date ? format(formData.check_in_date, 'PPP') : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8E9A95]">Check-out</span>
                  <span className="text-[#E8E3D9]">{formData.check_out_date ? format(formData.check_out_date, 'PPP') : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8E9A95]">Nights</span>
                  <span className="text-[#E8E3D9]">{nights()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8E9A95]">Guests</span>
                  <span className="text-[#E8E3D9]">{formData.num_guests}</span>
                </div>
                {extraBedCount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#8E9A95]">Extra beds ({extraBedCount} × ₹1,000 × {nights()}n)</span>
                    <span className="text-[#E8E3D9]">₹{(extraBedCount * 1000 * nights()).toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#D4AF37]/20">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-[#C4BFB1]">Est. Total</span>
                  <div className="flex items-center text-[#D4AF37]">
                    <IndianRupee size={18} />
                    <span className="text-2xl numeral">{calculateTotal().toLocaleString()}</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#8E9A95] mt-1 tracking-wider">
                  Includes room rate + extra bed · Taxes extra · Final amount confirmed by our team
                </p>
              </div>
            </div>

            <p className="text-[12px] text-[#8E9A95]">
              Includes Bed & Breakfast · Lunch, dinner, BBQ & safari at additional cost
            </p>

            {errors.submit && <p className="text-red-400 text-xs text-center">{errors.submit}</p>}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 border-[#D4AF37]/30 bg-transparent text-[#C4BFB1] hover:bg-[#1A2421] hover:text-[#E8E3D9] uppercase tracking-[0.2em] text-xs"
                data-testid="booking-back-step3"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C766] text-[#0B0F0E] uppercase tracking-[0.2em] text-xs font-semibold"
                data-testid="booking-submit-btn"
              >
                {loading ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && success && (
          <div className="text-center py-6">
            <div className="w-14 h-14 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-[#D4AF37]" />
            </div>
            <h3 className="font-serif text-2xl text-[#E8E3D9] mb-2 italic">Thank you.</h3>
            <p className="text-[#8E9A95] mb-6 text-sm leading-relaxed">
              Your enquiry has reached us. We'll be in touch shortly to confirm.
            </p>
            <div className="bg-[#0B0F0E] border border-[#D4AF37]/30 p-4 text-left mb-6 space-y-1.5">
              <p className="text-xs text-[#8E9A95]">
                <span className="text-[#D4AF37]">Call · WhatsApp</span> +91 83108 38799 · +91 86182 85170
              </p>
              <p className="text-xs text-[#8E9A95]">
                <span className="text-[#D4AF37]">Email</span> info@tuskermark.com
              </p>
            </div>
            <Button
              onClick={onClose}
              className="w-full bg-[#D4AF37] hover:bg-[#E5C766] text-[#0B0F0E] uppercase tracking-[0.2em] text-xs font-semibold"
              data-testid="booking-close-success"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
